import { LoaderFunctionArgs, redirect, RouteObject } from 'react-router';
import { lazyRoute } from '@repo/ui/lib/lazy-route';

import { anonymousLoader, protectedLoader } from './lib/auth-loader';

import { route as routeCommonLayout } from './layout/common-layout';
import { route as routeAppLayout } from './layout/app-layout';
import { ClubErrorBoundary, ErrorBoundary } from './pages/error-boundy';
import {
  cashierLayoutId,
  idPublicTournamentIndex,
  tournamentModeLayoutId,
  tournamentRouteId,
} from './route-ids';
import {
  urlCashierMode,
  urlCategoryList,
  urlStock,
  urlActivateStock,
  urlStockEntry,
  urlConfig,
  urlControlPanel,
  urlCourt,
  urlCourtList,
  urlCourtReservations,
  urlEditCategory,
  urlEditProduct,
  urlLogin,
  urlOpenMatches,
  urlPlayerList,
  urlProductList,
  urlProfile,
  urlPublicOpenMatch,
  urlPublicTournament,
  urlRanking,
  urlSales,
  urlScreen,
  urlScreenDevice,
  urlScreenList,
  urlSponsors,
  urlSignup,
  urlNewTournament,
  urlTournament,
  urlTournamentConfirmRegister,
  urlTournamentList,
  urlTournamentMode,
  urlTournamentPrint,
} from './urls';
import { WEBSITE_URL } from './data/constants';

// Keeps the query string so the chosen category survives the redirect.
const redirectToTournamentRegistrations = ({ params, request }: LoaderFunctionArgs) =>
  redirect(
    urlTournament(params.id as string, params.tournamentId as string, 'inscriptos') +
      new URL(request.url).search
  );

export const routes: RouteObject[] = [
  {
    ...routeCommonLayout,
    errorElement: <ErrorBoundary />,
    children: [
      {
        id: idPublicTournamentIndex,
        path: urlPublicTournament(':tournamentId'),
        lazy: lazyRoute(() => import('./pages/public-tournament/page')),
      },
      {
        // Old links carried the state (/active, /finished, /open-inscriptions/…).
        path: `${urlPublicTournament(':tournamentId')}/*`,
        loader: ({ params, request }: LoaderFunctionArgs) =>
          redirect(
            urlPublicTournament(params.tournamentId as string) + new URL(request.url).search
          ),
      },
      {
        path: urlPublicOpenMatch(':matchId'),
        lazy: lazyRoute(() => import('./pages/public-open-match')),
      },
      {
        path: urlScreenDevice(),
        lazy: lazyRoute(() => import('./pages/screen-device/page')),
      },
      {
        // The registration WhatsApp template still links here.
        path: urlTournamentConfirmRegister(),
        loader: ({ request }: LoaderFunctionArgs) =>
          redirect(`${WEBSITE_URL}/confirmar-inscripcion${new URL(request.url).search}`),
      },
      {
        loader: anonymousLoader,
        children: [
          { path: urlLogin(), lazy: lazyRoute(() => import('./pages/login')) },
          { path: urlSignup(), lazy: lazyRoute(() => import('./pages/signup')) },
        ],
      },
      {
        loader: protectedLoader,
        // The session is checked on entering the protected area; expired tokens are
        // caught later by the API client's 401 auto-logout.
        shouldRevalidate: () => false,
        children: [
          { index: true, lazy: lazyRoute(() => import('./pages/club-selector')) },
          {
            ...routeAppLayout,
            children: [
              {
                // Below the shell: a blocked club keeps its sidebar, and with it the way to
                // the club's data and its subscription, which the API leaves open.
                errorElement: <ClubErrorBoundary />,
                children: [
                  {
                    path: urlControlPanel(':id'),
                    lazy: lazyRoute(() => import('./pages/control-panel')),
                  },
                  {
                    path: urlCourtList(':id'),
                    lazy: lazyRoute(() => import('./pages/court-list')),
                  },
                  {
                    path: urlCourt(':id', ':courtId'),
                    lazy: lazyRoute(() => import('./pages/court/layout')),
                    children: [
                      {
                        path: urlCourtReservations(':id', ':courtId', ':date?'),
                        lazy: lazyRoute(() => import('./pages/court/reservations')),
                      },
                    ],
                  },
                  {
                    path: urlTournamentList(':id'),
                    lazy: lazyRoute(() => import('./pages/tournament-list')),
                  },
                  {
                    path: urlNewTournament(':id'),
                    lazy: lazyRoute(() => import('./pages/new-tournament')),
                  },
                  {
                    id: tournamentRouteId,
                    path: urlTournament(':id', ':tournamentId'),
                    lazy: lazyRoute(() => import('./pages/tournament/layout')),
                    children: [
                      {
                        path: 'inscriptos',
                        lazy: lazyRoute(() => import('./pages/tournament/tabs/registrations')),
                      },
                      {
                        path: 'grupos',
                        lazy: lazyRoute(() => import('./pages/tournament/tabs/groups')),
                      },
                      {
                        path: 'cuadro',
                        lazy: lazyRoute(() => import('./pages/tournament/tabs/draw')),
                      },
                      {
                        path: 'estado',
                        lazy: lazyRoute(() => import('./pages/tournament/tabs/status')),
                      },
                      {
                        path: 'franjas',
                        lazy: lazyRoute(() => import('./pages/tournament/tabs/slots')),
                      },
                      {
                        path: 'datos',
                        lazy: lazyRoute(() => import('./pages/tournament/tabs/details')),
                      },
                      {
                        path: 'categorias',
                        lazy: lazyRoute(() => import('./pages/tournament/tabs/categories')),
                      },
                      // The index and the old per-status URLs (pending, open-inscriptions, active…).
                      { index: true, loader: redirectToTournamentRegistrations },
                      { path: ':status', loader: redirectToTournamentRegistrations },
                    ],
                  },
                  { path: urlRanking(':id'), lazy: lazyRoute(() => import('./pages/ranking')) },
                  { path: urlProfile(':id'), lazy: lazyRoute(() => import('./pages/profile')) },

                  // Product routes
                  {
                    path: urlProductList(':id'),
                    lazy: lazyRoute(() => import('./pages/product/layout')),
                    children: [
                      {
                        path: '',
                        lazy: lazyRoute(() => import('./pages/product/product-list')),
                        children: [
                          {
                            path: 'nuevo',
                            lazy: lazyRoute(
                              () => import('./pages/product/product-list/create-product')
                            ),
                          },
                        ],
                      },
                      {
                        path: urlCategoryList(':id'),
                        lazy: lazyRoute(() => import('./pages/product/category-list')),
                      },
                      {
                        path: urlStock(':id'),
                        lazy: lazyRoute(() => import('./pages/product/stock')),
                      },
                    ],
                  },
                  {
                    path: urlActivateStock(':id'),
                    lazy: lazyRoute(() => import('./pages/product/stock/activate')),
                  },
                  {
                    path: urlStockEntry(':id'),
                    lazy: lazyRoute(() => import('./pages/product/stock/entry')),
                  },
                  {
                    path: urlEditProduct(':id', ':productId'),
                    lazy: lazyRoute(() => import('./pages/product/edit-product')),
                  },
                  {
                    path: urlEditCategory(':id', ':categoryId'),
                    lazy: lazyRoute(() => import('./pages/product/edit-category')),
                  },

                  {
                    lazy: lazyRoute(() => import('./pages/config/layout')),
                    children: [
                      {
                        path: urlConfig(':id'),
                        lazy: lazyRoute(() => import('./pages/config/config')),
                      },
                      {
                        path: urlConfig(':id', 'pos'),
                        lazy: lazyRoute(() => import('./pages/config/pos')),
                      },
                      {
                        path: urlConfig(':id', 'usuarios'),
                        lazy: lazyRoute(() => import('./pages/config/users')),
                      },
                      {
                        path: urlConfig(':id', 'suscripcion'),
                        lazy: lazyRoute(() => import('./pages/config/subscription')),
                      },
                    ],
                  },

                  {
                    path: urlSales(':id'),
                    lazy: lazyRoute(() => import('./pages/product/sales/route')),
                  },

                  {
                    path: urlPlayerList(':id'),
                    lazy: lazyRoute(() => import('./pages/players/route')),
                  },

                  {
                    path: urlOpenMatches(':id'),
                    lazy: lazyRoute(() => import('./pages/open-matches/open-matches-list')),
                  },

                  {
                    path: urlSponsors(':id'),
                    lazy: lazyRoute(() => import('./pages/sponsors')),
                  },
                  {
                    path: urlScreenList(':id'),
                    lazy: lazyRoute(() => import('./pages/screens/screen-list')),
                  },
                  {
                    path: urlScreen(':id', ':screenId'),
                    lazy: lazyRoute(() => import('./pages/screens/screen-editor')),
                  },
                ],
              },
            ],
          },
          // Cashier mode (separate layout, no sidebar)
          {
            id: cashierLayoutId,
            path: urlCashierMode(':id'),
            lazy: lazyRoute(() => import('./layout/cashier-layout')),
            children: [
              {
                index: true,
                lazy: lazyRoute(() => import('./pages/cashier/cashier-page')),
                errorElement: <ClubErrorBoundary />,
              },
            ],
          },
          // The printable tournament: only the sheets, no sidebar.
          {
            path: urlTournamentPrint(':id', ':tournamentId'),
            lazy: lazyRoute(() => import('./pages/tournament-print/page')),
            errorElement: <ClubErrorBoundary />,
          },
          // Tournament mode (separate layout, no sidebar)
          {
            id: tournamentModeLayoutId,
            path: urlTournamentMode(':id', ':tournamentId'),
            lazy: lazyRoute(() => import('./layout/tournament-mode-layout')),
            // Its own loader already reads the club's tournaments.
            errorElement: <ClubErrorBoundary />,
            children: [
              {
                index: true,
                lazy: lazyRoute(() => import('./pages/tournament-mode/load-result')),
              },
              {
                path: 'canchas',
                lazy: lazyRoute(() => import('./pages/tournament-mode/courts')),
              },
            ],
          },
        ],
      },
    ],
  },
];
